import React, { useEffect, useRef, useState } from 'react';
import useContainerScale from './useContainerScale';
import { Box, IconButton as MuiIconButton, List, ListItem, ListItemButton } from '@mui/material';
import { KeyboardArrowLeft, KeyboardArrowRight } from '@mui/icons-material';
import { Icon, IconButton } from 'src/mtska-frontend-app-component/assets';

const ComponentSlider = ({
    report,
    maxWidth = '100%',
    height = 'auto',
    showTopPagination = false,
    /** Ridimensiona proporzionalmente tutto il contenuto allo spazio disponibile (senza media query per figlio). */
    scaleToFit = true,
    designWidth = 1280,
    designHeight = 720,
    minScale = 0.65,
}) => {
    const sections = Array.isArray(report) ? report : [];
    const [activeSection, setActiveSection] = useState(0);
    const [activeStep, setActiveStep] = useState(0);
    const [maxSteps, setMaxSteps] = useState(0);

    const [activeSectionStep, setActiveSectionStep] = useState(0);
    const [maxSectionStep, setMaxSectionStep] = useState(0);

    /** Accordion: il capitolo della pagina attiva resta espanso. */
    const [expandedSectionIndex, setExpandedSectionIndex] = useState(0);

    /** Sidebar indice: open (testo + icone) | condensed (solo icone). */
    const [sidebarOpen, setSidebarOpen] = useState(true);

    useEffect(() => {
        setMaxSteps(
            sections.reduce((total, section) => total + (Array.isArray(section?.pages) ? section.pages.length : 0), 0),
        );
    }, [sections]);

    useEffect(() => {
        if (sections.length === 0) {
            setActiveSection(0);
            setActiveSectionStep(0);
            setExpandedSectionIndex(null);
            return;
        }
        if (activeSection >= sections.length) {
            setActiveSection(0);
            setActiveSectionStep(0);
            setExpandedSectionIndex(0);
            return;
        }
        setExpandedSectionIndex(activeSection);
    }, [sections, activeSection]);

    useEffect(() => {
        const pages = sections[activeSection]?.pages || [];
        setMaxSectionStep(pages.length);
        if (activeSectionStep >= pages.length) {
            setActiveSectionStep(0);
        }
    }, [sections, activeSection, activeSectionStep]);

    useEffect(() => {
        let previousPages = 0;
        for (let i = 0; i < activeSection; i++) {
            previousPages += sections[i]?.pages?.length || 0;
        }
        setActiveStep(previousPages + activeSectionStep);
    }, [sections, activeSection, activeSectionStep]);

    const handleNext = () => {
        const currentSection = sections[activeSection];
        if (!currentSection || !Array.isArray(currentSection.pages) || currentSection.pages.length === 0) return;
        const nextPageIndex = activeSectionStep + 1;

        if (nextPageIndex < currentSection.pages.length) {
            setActiveSectionStep(nextPageIndex);
            setExpandedSectionIndex(activeSection);
        } else {
            const nextSectionIndex = activeSection + 1;
            if (nextSectionIndex < sections.length) {
                setActiveSection(nextSectionIndex);
                setActiveSectionStep(0);
                setExpandedSectionIndex(nextSectionIndex);
            } else {
                setActiveSection(0);
                setActiveSectionStep(0);
                setExpandedSectionIndex(0);
            }
        }
    };

    const handleBack = () => {
        if (sections.length === 0) return;
        if (activeSectionStep > 0) {
            setActiveSectionStep(activeSectionStep - 1);
            setExpandedSectionIndex(activeSection);
        } else {
            const prevSectionIndex = activeSection - 1;
            if (prevSectionIndex >= 0) {
                const lastPage = (sections[prevSectionIndex]?.pages?.length || 1) - 1;
                setActiveSection(prevSectionIndex);
                setActiveSectionStep(lastPage);
                setExpandedSectionIndex(prevSectionIndex);
            } else {
                const lastSectionIndex = sections.length - 1;
                const lastPage = (sections[lastSectionIndex]?.pages?.length || 1) - 1;
                setActiveSection(lastSectionIndex);
                setActiveSectionStep(lastPage);
                setExpandedSectionIndex(lastSectionIndex);
            }
        }
    };

    const goTo = (section, page) => {
        setActiveSection(section);
        setActiveSectionStep(page);
        setExpandedSectionIndex(section);
    };

    const activePage = sections[activeSection]?.pages?.[activeSectionStep];
    const canNavigate = maxSectionStep > 0 && !!activePage;

    const viewportRef = useRef(null);
    const scale = useContainerScale(viewportRef, {
        enabled: scaleToFit,
        designWidth,
        designHeight: height === 'auto' ? null : designHeight,
        minScale,
    });

    const slider = (
        <Box
            className={`mtska-slider${sidebarOpen ? '' : ' mtska-slider--condensed'}`}
        >
            <Box className="mtska-slider-sidebar">
                <IconButton
                    className="mtska-slider-sidebar-toggle"
                    icon={sidebarOpen ? 'faArrowLeftFromLine' : 'faArrowRightFromLine'}
                    family="fas"
                    onClick={() => setSidebarOpen((open) => !open)}
                />

                <List className="mtska-slider-page-index">
                    {sections.map((section, index) => (
                        <React.Fragment key={index}>
                            <ListItem disablePadding>
                                <ListItemButton
                                    onClick={() => goTo(index, 0)}
                                    selected={expandedSectionIndex === index}
                                    className="mtska-slider-section-header"
                                >
                                    <Icon icon={section.icon} className="icon" />
                                    <span className="mtska-slider-item-label">{section.title}</span>
                                </ListItemButton>
                            </ListItem>
                            {expandedSectionIndex === index && (
                                <List disablePadding>
                                    {section.pages.map((page, pageIndex) => (
                                        <ListItem
                                            key={pageIndex}
                                            sx={{ pl: sidebarOpen ? 4 : 2, cursor: 'pointer' }}
                                            onClick={() => goTo(index, pageIndex)}
                                            className={
                                                'mtska-slider-page-item active' +
                                                (pageIndex === activeSectionStep && index === activeSection ? ' current' : '')
                                            }
                                        >
                                            <Icon icon={page.icon} className="icon" />
                                            <span className="mtska-slider-item-label">{page.title}</span>
                                        </ListItem>
                                    ))}
                                </List>
                            )}
                        </React.Fragment>
                    ))}
                </List>

                <Box className="mtska-slider-pager">
                    <MuiIconButton
                        size="small"
                        onClick={handleBack}
                        disabled={!canNavigate}
                        className="slider-arrow slider-arrow-left"
                    >
                        <KeyboardArrowLeft />
                    </MuiIconButton>
                    {sidebarOpen && (
                        <Box className="mtska-page-indicator">
                            {activeStep + 1} / {maxSteps}
                        </Box>
                    )}
                    <MuiIconButton
                        size="small"
                        onClick={handleNext}
                        disabled={!canNavigate}
                        className="slider-arrow slider-arrow-right"
                    >
                        <KeyboardArrowRight />
                    </MuiIconButton>
                </Box>
            </Box>

            <Box
                sx={{
                    width: '100%',
                    ...(height !== 'auto' ? { height } : {}),
                }}
                className="mtska-slider-page-wrapper"
            >
                {activePage?.component || null}
            </Box>
        </Box>
    );

    if (!scaleToFit) {
        return (
            <Box sx={{ maxWidth, position: 'relative', width: '100%' }}>
                {slider}
            </Box>
        );
    }

    return (
        <Box
            ref={viewportRef}
            className="mtska-slider-viewport"
            sx={{
                maxWidth,
                position: 'relative',
                width: '100%',
                minHeight: 0,
                overflow: 'hidden',
                ...(height !== 'auto' ? { height } : {}),
            }}
        >
            <Box
                className="mtska-slider-scaled"
                style={{ '--mtska-slider-scale': scale }}
            >
                {slider}
            </Box>
        </Box>
    );
};

export default ComponentSlider;